import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { getActiveTestPackageById, TestPackage } from '../services/testPackageService';
import { getActiveInsuranceById, Insurance } from '../services/insuranceService';
import { getActiveSupplementaryInsuranceById, SupplementaryInsurance } from '../services/supplementaryInsuranceService';
import { getCalendarByDate, getAvailableDays, generateTimeSlots, CalendarData, isCapacityFull, getBookedTimeSlots } from '../services/calendarService';
import API_CONFIG from '../config/api';
import { formatYmdPersian, parseYmdLocal } from '../utils/dateUtils';

// Transaction interface
interface Transaction {
  _id: string;
  amount: number;
  description: string;
  status: 'pending' | 'completed' | 'failed';
  gateway: string;
  refId?: string;
  paymentDate?: string;
  createdAt: string;
  transactionType: 'request' | 'remaining';
}

// Interface for the detailed test request data (adjust as per your actual API response)
interface DetailedTestRequest {
  _id: string;
  displayId?: number; // Display ID for tracking (شناسه)
  userId: {
    _id: string;
    name: string;
    lastName: string;
    mobile: string;
    nationalId: string;
  };
  patientInfo: {
    patientType: string;
    firstName: string;
    lastName: string;
    nationalId: string;
    mobile: string;
    gender: string;
  };
  samplingLocation: {
    type: string; // 'home' or 'lab'
    needsContainer?: boolean;
    freeTransportation?: boolean;
  };
  addressInfo: {
    region: string;
    field: string;
    street: string;
    alley?: string;
    building?: string;
    unit?: string;
    number?: string;
    coordinates?: [number, number];
  };
  importantNote?: string;
  ageInfo: {
    age: string; // Or number, adjust based on NewTestRequest
  };
  insuranceInfo: {
    type: string;
    nationalIdImageUrl?: string;
    testListImageUrl?: string;
    prescriptionImageUrl?: string;
    insuranceBookletImageUrl?: string;
    trackingCode?: string;
    description?: string;
    basicInsurance?: string;
    supplementaryInsurance?: string;
    selectedPackage?: string;
  };
  status: string;
  createdAt: string;
  updatedAt: string;
  rejectionReason?: string; // Reason provided when request is rejected
  instructions?: string; // Instructions provided when request is approved
  editDeadline?: string; // Edit deadline for rejected requests
  editVersion?: number; // Edit version number
  // Add any other fields that your API returns for a single request
  labResponseDateTime?: string; // Added for lab response datetime
  continueDateTime?: string; // Added for continue datetime
  paymentDateTime?: string; // Added for payment datetime
  amount?: number; // Amount to be paid (in Toman)
  transportationPrice?: number; // Transportation price (هزینه ایاب و ذهاب)
  paymentExtensionRequest?: boolean; // Payment extension request
  paymentExtensionStatus?: 'pending' | 'approved' | 'rejected'; // Payment extension status
  paymentExtensionRejectionReason?: string; // Payment extension rejection reason
  samplingDate?: string; // Selected sampling date
  samplingTime?: string; // Selected sampling time
}

// Helper function to translate status (can be moved to a shared utils file)
const translateStatus = (status: string, request?: any): string => {
  if (status === 'paid') {
    // Check if total amount (base + transportation) is 0 OR no transaction exists
    if (request) {
      const baseAmount = request.amount || 0;
      const transportationAmount = request.transportationPrice || 0;
      const totalAmount = baseAmount + transportationAmount;

      if (totalAmount === 0 || !request.transaction) {
        return 'نهایی شده';
      }
    }
    return 'نهایی و پرداخت شده';
  }

  const statusMap: Record<string, string> = {
    incomplete: 'ناتمام',
    canceled: 'لغو شده',
    reviewing: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    edited: 'ویرایش شده',
    expired: 'منقضی شده',
  };
  return statusMap[status] || status;
};

// Function to check if user can edit a rejected request
const canEditRequest = (editDeadline: string | undefined, status: string): boolean => {
  if (status !== 'rejected' || !editDeadline) return false;

  const deadline = new Date(editDeadline);
  const now = new Date();
  return now.getTime() <= deadline.getTime();
};

// Function to calculate remaining edit time for rejected requests
const calculateRemainingEditTime = (editDeadline: string | undefined, status: string): string => {
  if (status !== 'rejected' || !editDeadline) return '';

  const deadline = new Date(editDeadline);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();

  if (diffMs <= 0) return 'منقضی شده';

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHours > 0) {
    return `${diffHours} ساعت و ${diffMinutes} دقیقه`;
  } else {
    return `${diffMinutes} دقیقه`;
  }
};

const ViewRequestStatus: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [requestDetails, setRequestDetails] = useState<DetailedTestRequest | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<TestPackage | null>(null);
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(null);
  const [selectedSupplementaryInsurance, setSelectedSupplementaryInsurance] = useState<SupplementaryInsurance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [transportationPrice, setTransportationPrice] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<Array<{
    date: string;
    persianDate: string;
    dayName: string;
    isAvailable: boolean;
    isVacation: boolean;
    hasCapacity: boolean;
    calendarData: CalendarData | null;
  }>>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<Array<{ time: string, persianTime: string, available: boolean, isBooked: boolean }>>([]);
  const [bookedTimeSlots, setBookedTimeSlots] = useState<string[]>([]);
  const [calendarLoading, setCalendarLoading] = useState<boolean>(false);
  const [serverCalendarHour, setServerCalendarHour] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [requestingExtension, setRequestingExtension] = useState<boolean>(false);
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const isPayingRef = useRef(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);

  // Fetch transportation price
  const fetchTransportationPrice = async () => {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/settings`);
      const data = await response.json();
      if (data.success) {
        setTransportationPrice(data.data.transportationPrice || 0);
      }
    } catch (error) {

    }
  };

  // Fetch transactions for this test request
  const fetchTransactions = async (testRequestId: string) => {
    try {
      setLoadingTransactions(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      const response = await fetch(`${API_CONFIG.baseURL}/payments/test-request/${testRequestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTransactions(data.data || []);
        }
      }
    } catch (error) {

    } finally {
      setLoadingTransactions(false);
    }
  };

  // Helper function to calculate total amount
  const calculateTotalAmount = () => {
    const baseAmount = requestDetails?.amount || 0;
    let totalAmount = baseAmount;

    // Add transportation cost only for home sampling and if not free
    if (requestDetails?.samplingLocation?.type === 'home' &&
      !requestDetails?.samplingLocation?.freeTransportation) {
      totalAmount += transportationPrice;
    }

    return totalAmount;
  };

  useEffect(() => {
    const fetchRequestDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
          setLoading(false);
          return;
        }

        // Use API_CONFIG to get the correct URL
        const response = await fetch(API_CONFIG.getUrl(`test-requests/${id}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'خطا در دریافت ');
        }

        const data = await response.json();
        setRequestDetails(data.data); // Assuming API returns data under a 'data' key

        // Fetch transactions for this request
        if (id) {
          fetchTransactions(id);
        }

        // If the request has a selected package, fetch its details
        if (data.data?.insuranceInfo?.selectedPackage) {
          try {
            const packageDetails = await getActiveTestPackageById(data.data.insuranceInfo.selectedPackage);
            setSelectedPackage(packageDetails);
          } catch (packageError) {

            // Don't set error state for package fetch failure, just log it
          }
        }

        // If the request has a basic insurance, fetch its details
        if (data.data?.insuranceInfo?.basicInsurance) {
          try {
            const insuranceDetails = await getActiveInsuranceById(data.data.insuranceInfo.basicInsurance);
            setSelectedInsurance(insuranceDetails);
          } catch (insuranceError) {

            // Don't set error state for insurance fetch failure, just log it
          }
        }

        // If the request has a supplementary insurance, fetch its details
        if (data.data?.insuranceInfo?.supplementaryInsurance && data.data.insuranceInfo.supplementaryInsurance !== 'none') {
          try {
            const supplementaryInsuranceDetails = await getActiveSupplementaryInsuranceById(data.data.insuranceInfo.supplementaryInsurance);
            setSelectedSupplementaryInsurance(supplementaryInsuranceDetails);
          } catch (supplementaryInsuranceError) {

            // Don't set error state for supplementary insurance fetch failure, just log it
          }
        }
      } catch (err: any) {
        setError(err.message || 'یک خطای ناشناخته رخ داد.');

      } finally {
        setLoading(false);
      }
    };

    fetchRequestDetails();
    fetchTransportationPrice();
  }, [id]);

  // Get sampling location type from request details
  const getSamplingLocationType = (): 'home' | 'lab' => {
    return requestDetails?.samplingLocation?.type === 'lab' ? 'lab' : 'home';
  };

  // Check if a date is available for home sampling considering the 14:00 cutoff
  // NOTE: This function is now mainly for compatibility since we don't render tomorrow after 14:00
  const isDateAvailableForHomeSampling = (dateString: string): boolean => {
    // Since we now skip rendering tomorrow for home sampling after 14:00,
    // all rendered dates should be available
    return true;
  };

  // Update time slots based on selected date (DEPRECATED - kept for compatibility)
  const updateTimeSlots = async (calendarData: CalendarData | null, capacityFull: boolean = false, bookedSlots?: string[]) => {

    // This function is now deprecated as the logic is handled in handleDateSelect
  };

  // Load selectable days from server (Asia/Tehran) — do not use browser clock
  const checkCalendarAvailability = async () => {
    if (!requestDetails) return;

    setCalendarLoading(true);
    const samplingType = getSamplingLocationType();

    try {
      const serverData = await getAvailableDays(samplingType);
      if (!serverData) {
        setAvailableDates([]);
        setServerCalendarHour(null);
        setCalendarLoading(false);
        return;
      }

      setServerCalendarHour(serverData.serverHour);

      const days = serverData.days.map((day) => {
        const localDate = parseYmdLocal(day.date);
        const persianDate = new Intl.DateTimeFormat('fa-IR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(localDate);
        const dayName = new Intl.DateTimeFormat('fa-IR', { weekday: 'long' }).format(localDate);

        const calendarData: CalendarData | null = {
          _id: '',
          date: day.date,
          startTime: day.startTime || '08:00',
          endTime: day.endTime || '18:00',
          capacity: day.maxCapacity,
          isVacation: day.isVacation,
          type: samplingType,
          createdAt: '',
          updatedAt: ''
        };

        return {
          date: day.date,
          persianDate,
          dayName,
          isAvailable: day.isAvailable,
          isVacation: day.isVacation,
          hasCapacity: day.hasCapacity,
          calendarData
        };
      });

      setAvailableDates(days);
      setCalendarLoading(false);

      const firstAvailable = days.find(day => day.isAvailable);
      if (firstAvailable && !selectedDate) {
        // Pass freshly built days — React state is not updated yet
        await handleDateSelect(firstAvailable.date, days);
      }
    } catch {
      setAvailableDates([]);
      setServerCalendarHour(null);
      setCalendarLoading(false);
    }
  };

  // DEPRECATED: fetchBookedTimeSlots - logic moved to handleDateSelect
  // Keeping for potential cleanup later

  // Handle date selection
  const handleDateSelect = async (
    date: string,
    daysSource?: Array<{
      date: string;
      persianDate: string;
      dayName: string;
      isAvailable: boolean;
      isVacation: boolean;
      hasCapacity: boolean;
      calendarData: CalendarData | null;
    }>
  ) => {

    const selectedDateData = (daysSource || availableDates).find(d => d.date === date);
    if (!selectedDateData || !selectedDateData.isAvailable) {

      return;
    }

    // Since we now skip rendering problematic dates, this check is no longer needed
    // All rendered dates should be selectable

    // Clear previous selections
    setSelectedDate(date);
    setSelectedTime('');
    setAvailableTimeSlots([]); // Clear existing slots immediately

    const samplingType = getSamplingLocationType();

    try {

      const calendarData = await getCalendarByDate(date, samplingType);

      const capacityFull = await isCapacityFull(date, samplingType);

      const bookedSlots = await getBookedTimeSlots(date, samplingType);

      // Update state with booked slots
      setBookedTimeSlots(bookedSlots);

      // Generate base time slots
      const baseSlots = generateTimeSlots(calendarData, capacityFull);

      // Apply booked slots to disable them
      const finalSlots = baseSlots.map(slot => {
        const isBooked = bookedSlots.includes(slot.time);
        const isAvailable = slot.available && !isBooked;

        return {
          ...slot,
          available: isAvailable,
          isBooked: isBooked
        };
      });

      setAvailableTimeSlots(finalSlots);

      // Auto-select first available time slot
      const availableSlots = finalSlots.filter(slot => slot.available);
      if (availableSlots.length > 0) {
        setSelectedTime(availableSlots[0].time);

      } else {

      }

    } catch (error: any) {

      // Fallback to defaults
      setBookedTimeSlots([]);
      setAvailableTimeSlots([]);
      const fallbackSlots = generateTimeSlots(null, false);
      setAvailableTimeSlots(fallbackSlots);
    }
  };

  // Check calendar availability when request details are loaded and status is approved
  useEffect(() => {
    if (requestDetails?.status === 'approved') {
      checkCalendarAvailability();
    }
  }, [requestDetails]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="card bg-white rounded-sm p-6 border border-slate-300 text-center">
            <p>در حال بارگذاری ...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="card bg-white rounded-sm p-6 border border-slate-300 text-center">
            <p className="text-red-500">خطا: {error}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!requestDetails) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="card bg-white rounded-sm p-6 border border-slate-300 text-center">
            <p>ی یافت نشد.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Helper function to render uploaded image preview (adapted from NewTestRequest)
  const renderImagePreview = (imageUrl: string | undefined, title: string) => {
    if (!imageUrl) return null;
    return (
      <div className="mb-4">
        <h5 className="font-medium text-sm mb-2">{title}</h5>
        <div className="border border-slate-300 rounded-sm p-2 bg-slate-50">
          <img
            src={`${API_CONFIG.uploadURL}${imageUrl}`}
            alt={title}
            className="max-h-40 object-contain mx-auto cursor-pointer"
            onClick={() => {
              setModalImage(`${API_CONFIG.uploadURL}${imageUrl}`);
              setModalOpen(true);
            }}
          />
        </div>
      </div>
    );
  };

  // Build insurance type display name (adapted from NewTestRequest)
  const getInsuranceTypeDisplay = (type: string) => {
    switch (type) {
      case 'free': return 'آزاد';
      case 'paper': return 'دفترچه بیمه';
      case 'electronic': return 'نسخه الکترونیک';
      case 'package': return 'پکیج آزمایشگاه';
      default: return type;
    }
  };

  // Get basic insurance display name (using real insurance data)
  const getBasicInsuranceDisplay = (insuranceId: string | undefined) => {
    if (!insuranceId) return '';
    if (insuranceId === 'none') return 'هیچکدام';

    // Use the fetched insurance data if available
    if (selectedInsurance && selectedInsurance._id === insuranceId) {
      return selectedInsurance.name;
    }

    // If insurance data not available, show "حذف شده" (deleted)
    return 'حذف شده';
  };

  // Get supplementary insurance display name (using real supplementary insurance data)
  const getSupplementaryInsuranceDisplay = (supplementaryInsurance: string | undefined) => {
    if (!supplementaryInsurance) return '';
    if (supplementaryInsurance === 'none') return 'هیچکدام';

    // Use the fetched supplementary insurance data if available
    if (selectedSupplementaryInsurance && selectedSupplementaryInsurance._id === supplementaryInsurance) {
      return selectedSupplementaryInsurance.name;
    }

    // If insurance data not available, show "حذف شده" (deleted)
    return 'حذف شده';
  };

  // Function to translate patient type to Persian (can be moved to a shared utils file)
  const translatePatientType = (type: string): string => {
    const typeMap: Record<string, string> = {
      self: 'خودم',
      child: 'فرزند',
      spouse: 'همسر',
      father: 'پدر', // Assuming 'parent' might be split or covered by father/mother
      mother: 'مادر',
      parent: 'والدین', // Added for completeness if 'parent' is used
      other: 'سایر'
    };
    return typeMap[type] || type;
  };

  // Function to request payment extension
  const handleRequestPaymentExtension = async () => {
    if (!requestDetails) return;

    try {
      setRequestingExtension(true);
      const token = localStorage.getItem('authToken');

      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
        return;
      }

      const response = await fetch(`${API_CONFIG.baseURL}/test-requests/${requestDetails._id}/request-payment-extension`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update the local state to reflect the change
          setRequestDetails(prev => prev ? { ...prev, paymentExtensionRequest: true } : null);
          alert('درخواست تمدید پرداخت با موفقیت ارسال شد');
        } else {
          setError(data.message || 'خطا در ارسال درخواست تمدید پرداخت');
        }
      } else {
        setError('خطا در ارتباط با سرور');
      }
    } catch (err) {

      setError('خطا در ارتباط با سرور');
    } finally {
      setRequestingExtension(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        <div className="card bg-white rounded-sm p-5 md:p-6 border border-slate-300">
          <h2 className="text-xl font-bold text-slate-800 mb-6">جزئیات درخواست آزمایش</h2>

          {/* Status Display */}
          <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-700">
              وضعیت فعلی درخواست: {translateStatus(requestDetails.status, requestDetails)}
            </h3>
          </div>

          {/* Payment Extension Request Section - Only shown when status is expired, no extension already requested, and within 24 hours of approval */}
          {requestDetails.status === 'expired' && !requestDetails.paymentExtensionRequest && (() => {
            // Check if within 24 hours of approval
            const approvalTime = requestDetails.labResponseDateTime ? new Date(requestDetails.labResponseDateTime) : null;
            const now = new Date();
            const twentyFourHoursMs = 24 * 60 * 60 * 1000;
            const canRequestExtension = approvalTime && (now.getTime() - approvalTime.getTime()) < twentyFourHoursMs;

            if (canRequestExtension) {
              return (
                <div className="mb-6 p-4 rounded-md bg-yellow-50 border border-yellow-200">
                  <h3 className="text-lg font-semibold text-yellow-700 mb-2">
                    <span className="ml-2">⏰</span>
                    درخواست تمدید پرداخت
                  </h3>
                  <div className="text-yellow-700 bg-white p-3 rounded border border-yellow-200">
                    <p className="mb-3">
                      مهلت پرداخت این درخواست به پایان رسیده است. آیا می‌خواهید درخواست تمدید مهلت پرداخت کنید؟
                    </p>
                    <button
                      onClick={handleRequestPaymentExtension}
                      disabled={requestingExtension}
                      className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md font-medium transition-colors disabled:bg-yellow-400 disabled:cursor-not-allowed"
                    >
                      {requestingExtension ? 'در حال ارسال...' : '⏰ درخواست تمدید پرداخت'}
                    </button>
                  </div>
                </div>
              );
            } else {
              // Show message that extension period has ended
              return (
                <div className="mb-6 p-4 rounded-md bg-gray-50 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-500 mb-2">
                    <span className="ml-2">⛔</span>
                    مهلت درخواست تمدید تمام شده
                  </h3>
                  <div className="text-gray-500 bg-white p-3 rounded border border-gray-200">
                    <p>
                      متأسفانه بیش از ۲۴ ساعت از تایید اولیه درخواست گذشته و امکان درخواست تمدید وجود ندارد.
                    </p>
                  </div>
                </div>
              );
            }
          })()}

          {/* Payment Extension Request Status - Show if there was ever a payment extension request */}
          {requestDetails.paymentExtensionStatus && (
            <div className={`mb-6 p-4 rounded-md border ${requestDetails.paymentExtensionStatus === 'approved'
                ? 'bg-green-50 border-green-200'
                : requestDetails.paymentExtensionStatus === 'rejected'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
              <h3 className={`text-lg font-semibold mb-2 ${requestDetails.paymentExtensionStatus === 'approved'
                  ? 'text-green-700'
                  : requestDetails.paymentExtensionStatus === 'rejected'
                    ? 'text-red-700'
                    : 'text-blue-700'
                }`}>
                <span className="ml-2">
                  {requestDetails.paymentExtensionStatus === 'approved'
                    ? '✅'
                    : requestDetails.paymentExtensionStatus === 'rejected'
                      ? '❌'
                      : '⏳'
                  }
                </span>
                وضعیت درخواست تمدید پرداخت
              </h3>
              <div className={`bg-white p-3 rounded border ${requestDetails.paymentExtensionStatus === 'approved'
                  ? 'text-green-700 border-green-200'
                  : requestDetails.paymentExtensionStatus === 'rejected'
                    ? 'text-red-700 border-red-200'
                    : 'text-blue-700 border-blue-200'
                }`}>
                {requestDetails.paymentExtensionStatus === 'approved' ? (
                  <>
                    <p className="mb-2 font-medium">
                      ✅ درخواست تمدید مهلت پرداخت شما تایید شده است.
                    </p>
                    <p className="text-sm text-green-600">
                      مهلت پرداخت شما تمدید شده و می‌توانید پرداخت خود را انجام دهید.
                    </p>
                  </>
                ) : requestDetails.paymentExtensionStatus === 'rejected' ? (
                  <>
                    <p className="mb-2 font-medium">
                      ❌ درخواست تمدید مهلت پرداخت شما رد شده است.
                    </p>
                    {requestDetails.paymentExtensionRejectionReason && (
                      <div className="mt-2 p-2 bg-red-100 rounded border border-red-300">
                        <strong className="text-sm">دلیل رد:</strong>
                        <p className="text-sm mt-1">{requestDetails.paymentExtensionRejectionReason}</p>
                      </div>
                    )}
                    {!requestDetails.paymentExtensionRejectionReason && (
                      <p className="text-sm text-red-600">
                        متاسفانه درخواست تمدید مهلت پرداخت شما تایید نشده است.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="mb-2">
                      درخواست تمدید مهلت پرداخت شما ارسال شده و در انتظار تایید مدیریت است.
                    </p>
                    <p className="text-sm text-blue-600">
                      پس از تایید، مهلت پرداخت شما تمدید خواهد شد.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Important Note Display - Show if important note exists */}
          {requestDetails.importantNote && (
            <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-300">
              <h3 className="text-lg font-bold text-red-700 mb-2">
                <span className="ml-2">⚠️</span>
                نکته مهم:
              </h3>
              <div className="text-red-800 bg-white p-3 rounded border border-red-300">
                <p className="font-bold text-red-700">
                  {requestDetails.importantNote}
                </p>
              </div>
            </div>
          )}

          {/* Instructions Display - Show if instructions exist (regardless of current status) */}
          {requestDetails.instructions && (
            <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200">
              <h4 className="text-sm font-semibold text-green-700 mb-2">
                <span className="ml-2">📋</span>
                {requestDetails.status === 'approved' ? 'دستورالعمل‌ها:' : 'دستورالعمل‌های قبلی:'}
              </h4>
              <div className="text-green-700 bg-white p-2 rounded border border-green-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.instructions.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-green-600 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Rejection Reason Display - Show if rejection reason exists (regardless of current status) */}
          {requestDetails.rejectionReason && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
              <h4 className="text-sm font-semibold text-red-700 mb-2">
                {requestDetails.status === 'rejected' ? 'دلایل رد درخواست:' : 'دلایل رد قبلی:'}
              </h4>
              <div className="text-red-600 bg-white p-2 rounded border border-red-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.rejectionReason.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-red-500 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Edit Deadline and Edit Button - Only shown when status is rejected and edit deadline exists */}
          {requestDetails.status === 'rejected' && requestDetails.editDeadline && (
            <div className="mb-6 p-4 rounded-md bg-yellow-50 border border-yellow-200">
              <h3 className="text-lg font-semibold text-yellow-700 mb-2">
                <span className="ml-2">⏰</span>
                فرصت ویرایش درخواست
              </h3>
              <div className="text-yellow-700 bg-white p-3 rounded border border-yellow-200">
                {canEditRequest(requestDetails.editDeadline, requestDetails.status) ? (
                  <div>
                    <p className="mb-3">
                      <strong>زمان باقی‌مانده:</strong> {calculateRemainingEditTime(requestDetails.editDeadline, requestDetails.status)}
                    </p>
                    <Link
                      to={`/edit-request/${requestDetails._id}`}
                      className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                    >
                      ✏️ ویرایش درخواست
                    </Link>
                  </div>
                ) : (
                  <div>
                    <p className="text-red-600 font-medium mb-2">
                      <span className="ml-1">❌</span>
                      زمان ویرایش منقضی شده
                    </p>
                    <p className="text-sm text-gray-600">
                      مهلت ویرایش در {new Intl.DateTimeFormat('fa-IR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }).format(new Date(requestDetails.editDeadline))} به پایان رسیده است.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Continue Button for Incomplete Requests */}
          {requestDetails.status === 'incomplete' && (
            <div className="mb-6 p-4 rounded-md bg-orange-50 border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-700 mb-2">
                <span className="ml-2">📝</span>
                درخواست ناقص
              </h3>
              <div className="text-orange-700 bg-white p-3 rounded border border-orange-200">
                <p className="mb-3">
                  این درخواست هنوز کامل نشده است. برای تکمیل و ارسال درخواست خود، روی دکمه زیر کلیک کنید.
                </p>
                <Link
                  to={`/edit-request/${requestDetails._id}`}
                  className="inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors"
                >
                  📋 ادامه تکمیل درخواست
                </Link>
              </div>
            </div>
          )}

          {/* Appointment Scheduling - Only shown when status is approved */}
          {requestDetails.status === 'approved' && (
            <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-700 mb-4">
                <span className="ml-2">📅</span>
                انتخاب زمان نمونه‌گیری
              </h3>

              {/* Date Selection */}
              <div className="mb-4">
                <h4 className="font-medium text-blue-800 mb-3">انتخاب تاریخ:</h4>
                {getSamplingLocationType() === 'home' && serverCalendarHour !== null && serverCalendarHour >= 14 && (
                  <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <span className="text-lg">⏰</span>
                      <div className="text-sm">
                        <strong>توجه:</strong> برای درخواست‌های منزل، پس از ساعت 14:00 امکان رزرو برای فردا وجود ندارد. تاریخ‌های نمایش داده شده از پس فردا شروع می‌شوند.
                      </div>
                    </div>
                  </div>
                )}
                {calendarLoading ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-2 text-sm text-gray-600">در حال بررسی تقویم...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {availableDates.map((day) => (
                      <button
                        key={day.date}
                        onClick={() => handleDateSelect(day.date)}
                        disabled={!day.isAvailable}
                        className={`p-3 rounded-lg border-2 transition-all duration-200 text-center relative ${selectedDate === day.date
                          ? 'border-blue-500 bg-blue-100 text-blue-800'
                          : day.isAvailable
                            ? 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                            : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                      >
                        <div className="font-medium text-sm">{day.dayName}</div>
                        <div className="text-xs mt-1 opacity-75">{day.persianDate.split('،')[1]?.trim()}</div>
                        {day.isVacation && (
                          <div className="absolute top-1 right-1 text-red-500 text-xs">تعطیل</div>
                        )}
                        {!day.isVacation && !day.hasCapacity && (
                          <div className="absolute top-1 right-1 text-orange-500 text-xs">تکمیل</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Time Selection */}
              <div className="mb-4">
                <h4 className="font-medium text-blue-800 mb-3">انتخاب ساعت:</h4>
                {availableTimeSlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-32 overflow-y-auto">
                    {availableTimeSlots.map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() => {
                          if (slot.available) {

                            setSelectedTime(slot.time);
                          } else {

                          }
                        }}
                        disabled={!slot.available}
                        className={`p-2 rounded-md border text-sm transition-all duration-200 relative ${!slot.available
                            ? slot.isBooked
                              ? 'border-red-400 bg-red-100 text-red-600 cursor-not-allowed'
                              : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : selectedTime === slot.time
                              ? 'border-blue-500 bg-blue-100 text-blue-800'
                              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                          }`}
                      >
                        <div className="flex flex-col items-center">
                          <span>{slot.persianTime}</span>
                          {slot.isBooked && (
                            <span className="text-xs text-red-600 font-medium mt-1">رزرو شده</span>
                          )}
                        </div>
                        {slot.isBooked && (
                          <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : selectedDate ? (
                  <div className="text-center py-4 text-gray-500">
                    ساعت کاری برای این روز تعریف نشده است
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    ابتدا تاریخ را انتخاب کنید
                  </div>
                )}
              </div>

              {/* Selected DateTime Display */}
              {selectedDate && selectedTime && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                  <h5 className="font-medium text-blue-800 mb-1">زمان انتخاب شده:</h5>
                  <div className="text-blue-700">
                    <span className="ml-2">📅</span>
                    {availableDates.find(day => day.date === selectedDate)?.persianDate}
                    <span className="mx-2">-</span>
                    <span className="ml-2">🕐</span>
                    {availableTimeSlots.find(slot => slot.time === selectedTime)?.persianTime}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Amount Display - Show if amount exists (regardless of status) */}
          {requestDetails.amount !== undefined && requestDetails.amount !== null && (
            <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-700 mb-2">
                <span className="ml-2">💰</span>
                {requestDetails.status === 'approved' ? 'هزینه قابل پرداخت:' : 'هزینه تعیین شده:'}
              </h4>
              <div className="text-blue-700 bg-white p-2 rounded border border-blue-200 text-sm">
                <div className="text-lg font-bold text-blue-800">
                  {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} ریال`}
                </div>
              </div>
            </div>
          )}

          {/* Payment Section - Only shown when status is approved */}
          {requestDetails.status === 'approved' && (
            <div className="mb-6 p-4 rounded-md bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
              <h3 className="text-lg font-semibold text-green-700 mb-4">
                <span className="ml-2">💳</span>
                پرداخت هزینه آزمایش
              </h3>

              <div className="bg-white p-4 rounded-lg border border-green-200 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {/* Base Amount */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">هزینه آزمایش:</span>
                    <span className="font-medium">
                      {requestDetails.amount === 0
                        ? <span className="text-green-600">صفر (رایگان)</span>
                        : requestDetails.amount
                          ? `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} ریال`
                          : 'محاسبه شده'
                      }
                    </span>
                  </div>

                  {/* Transportation Cost */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">هزینه ایاب و ذهاب:</span>
                    <span className="font-medium">
                      {requestDetails.samplingLocation?.type === 'lab' ? (
                        <span className="text-green-600">رایگان (آزمایشگاه)</span>
                      ) : requestDetails.samplingLocation?.freeTransportation ? (
                        <span className="text-green-600">رایگان</span>
                      ) : (
                        <span>{new Intl.NumberFormat('fa-IR').format(transportationPrice)} ریال</span>
                      )}
                    </span>
                  </div>

                  {/* Total */}
                  <div className="md:col-span-2 border-t pt-2">
                    <div className="flex justify-between text-lg font-bold">
                      <span>مجموع:</span>
                      <span className="text-green-600">
                        {(() => {
                          const totalAmount = calculateTotalAmount();

                          // If total amount is 0, show "صفر (رایگان)"
                          if (totalAmount === 0) {
                            return 'صفر (رایگان)';
                          }

                          return new Intl.NumberFormat('fa-IR').format(totalAmount) + ' ریال';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={async () => {
                    if (isPayingRef.current) return;
                    isPayingRef.current = true;
                    setIsPaying(true);
                    try {
                      const token = localStorage.getItem('authToken');
                      if (!token) {
                        alert('لطفا ابتدا وارد شوید');
                        return;
                      }

                      const totalAmount = calculateTotalAmount();

                      // If total amount is 0, handle as free completion
                      if (totalAmount === 0) {
                        // For free requests, directly mark as paid
                        const response = await fetch(`${API_CONFIG.baseURL}/test-requests/${requestDetails._id}/complete-free`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            samplingDate: selectedDate,
                            samplingTime: selectedTime
                          })
                        });

                        if (response.ok) {
                          alert('درخواست با موفقیت تکمیل و ثبت شد');
                          window.location.reload();
                        } else {
                          alert('خطا در تکمیل درخواست');
                        }
                        return;
                      }

                      // No need to check requestDetails.amount since totalAmount already includes transportation
                      // The payment can proceed even if base amount is 0 but there's transportation cost

                      // Build payment request with current page URL for return
                      const currentPageUrl = window.location.href;

                      const paymentRequest = {
                        amount: totalAmount, // Use calculated total amount
                        description: `پرداخت هزینه نمونه گیری - شناسه ${requestDetails.displayId || requestDetails._id.slice(-8)}`,
                        callbackUrl: `${window.location.origin}/payment-callback`, // Backend will handle the returnUrl
                        email: '',
                        mobile: requestDetails.patientInfo.mobile,
                        userId: requestDetails.userId?._id || 'unknown', // Fallback for userId
                        planName: 'درخواست آزمایش',
                        planPeriod: 1,
                        basePrice: requestDetails.amount || 0, // Store original base price
                        discount: null,
                        returnUrl: currentPageUrl, // Pass returnUrl as separate parameter
                        samplingLocation: requestDetails.samplingLocation?.type, // Pass sampling location
                        freeTransportation: requestDetails.samplingLocation?.freeTransportation || false, // Pass free transportation flag
                        samplingDate: selectedDate, // Pass selected date
                        samplingTime: selectedTime, // Pass selected time
                        testRequestId: requestDetails._id // Pass test request ID
                      } as any;
                      const resp = await fetch(`${API_CONFIG.baseURL}/payments/create`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(paymentRequest)
                      });
                      const data = await resp.json();
                      if (data.success && data.url) {
                        window.location.href = data.url;
                      } else {
                        alert(data.message || 'خطا در ایجاد پرداخت');
                      }
                    } catch (e) {

                      alert('خطا در ایجاد پرداخت');
                    } finally {
                      isPayingRef.current = false;
                      setIsPaying(false);
                    }
                  }}
                  disabled={!selectedDate || !selectedTime || isPaying}
                  className={`flex-1 py-3 px-6 rounded-lg font-medium text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl ${calculateTotalAmount() === 0
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                    }`}
                >
                  <span className="ml-2">
                    {isPaying ? '⏳' : (calculateTotalAmount() === 0 ? '✅' : '💳')}
                  </span>
                  {isPaying
                    ? 'در حال ارسال...'
                    : (calculateTotalAmount() === 0 ? 'تکمیل و ثبت' : 'پرداخت آنلاین')}
                </button>

              </div>

              <p className="text-xs text-gray-600 mt-3 text-center">
                با انتخاب روش پرداخت، زمان نمونه‌گیری شما نهایی خواهد شد
              </p>
            </div>
          )}

          {/* Patient Info Section */}
          <div className="mb-8 border-b border-slate-200 pb-6">
            <h4 className="font-bold text-slate-700 mb-4 text-lg">مشخصات بیمار</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div><span className="font-medium text-slate-600">نام و نام خانوادگی:</span> {`${requestDetails.patientInfo.firstName} ${requestDetails.patientInfo.lastName}`}</div>
              <div><span className="font-medium text-slate-600">کد ملی:</span> {requestDetails.patientInfo.nationalId}</div>
              <div><span className="font-medium text-slate-600">شماره موبایل:</span> {requestDetails.patientInfo.mobile}</div>
              <div><span className="font-medium text-slate-600">جنسیت:</span> {requestDetails.patientInfo.gender === 'male' ? 'مرد' : 'زن'}</div>
              <div><span className="font-medium text-slate-600">نسبت بیمار:</span> {translatePatientType(requestDetails.patientInfo.patientType)}</div>
              <div><span className="font-medium text-slate-600">سن:</span> {requestDetails.ageInfo.age} سال</div>
            </div>
          </div>

          {/* Sampling Location Section */}
          <div className="mb-8 border-b border-slate-200 pb-6">
            <h4 className="font-bold text-slate-700 mb-4 text-lg">محل نمونه گیری</h4>
            <div className="text-sm">
              <p><span className="font-medium text-slate-600">نوع نمونه گیری:</span>
                {requestDetails.samplingLocation?.type === 'lab' ? ' نمونه گیری در آزمایشگاه' : ' نمونه گیری در منزل'}
              </p>

              {requestDetails.samplingLocation?.type === 'home' && (
                <>
                  {requestDetails.addressInfo && (
                    <>
                      <p className="mt-2"><span className="font-medium text-slate-600">منطقه:</span> {requestDetails.addressInfo.region}</p>
                      <p>
                        {requestDetails.addressInfo.field && `میدان ${requestDetails.addressInfo.field}، `}
                        {requestDetails.addressInfo.street && `خیابان ${requestDetails.addressInfo.street}، `}
                        {requestDetails.addressInfo.alley && `کوچه ${requestDetails.addressInfo.alley}، `}
                        {requestDetails.addressInfo.building && `ساختمان ${requestDetails.addressInfo.building}، `}
                        {requestDetails.addressInfo.unit && `واحد ${requestDetails.addressInfo.unit}، `}
                        {requestDetails.addressInfo.number && `پلاک ${requestDetails.addressInfo.number}`}
                      </p>
                    </>
                  )}

                  {/* Home Sampling Options */}
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center">
                      <span className="font-medium text-slate-600 ml-2">نیاز به ظرف:</span>
                      {requestDetails.samplingLocation.needsContainer ? (
                        <span className="text-green-600 font-medium">✓ دارد</span>
                      ) : (
                        <span className="text-red-600 font-medium">✗ ندارد</span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-slate-600 ml-2">ایاب و ذهاب:</span>
                      {requestDetails.samplingLocation.freeTransportation ? (
                        <span className="text-green-600 font-medium">✓ رایگان</span>
                      ) : (
                        <span className="text-red-600 font-medium">✗ با هزینه</span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {requestDetails.samplingLocation?.type === 'lab' && (
                <p className="mt-2 text-blue-600">
                  <span className="font-medium">📍</span> بیمار باید به آزمایشگاه مراجعه کند
                </p>
              )}
              {/* Map preview could be added here if coordinates are available and needed */}
            </div>
          </div>

          {/* Insurance Info Section */}
          <div className="mb-8">
            <h4 className="font-bold text-slate-700 mb-4 text-lg">اطلاعات بیمه و آزمایش</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm mb-4">
              <div><span className="font-medium text-slate-600">نوع درخواست:</span> {getInsuranceTypeDisplay(requestDetails.insuranceInfo.type)}</div>

              {requestDetails.insuranceInfo.type === 'paper' && (
                <>
                  <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance)}</div>
                  {requestDetails.insuranceInfo.supplementaryInsurance && (
                    <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance)}</div>
                  )}
                </>
              )}

              {requestDetails.insuranceInfo.type === 'electronic' && (
                <>
                  <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance)}</div>
                  {requestDetails.insuranceInfo.supplementaryInsurance && (
                    <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance)}</div>
                  )}
                  <div><span className="font-medium text-slate-600">کد رهگیری نسخه:</span> {requestDetails.insuranceInfo.trackingCode}</div>
                </>
              )}

              {requestDetails.insuranceInfo.type === 'package' && selectedPackage && (
                <div className="md:col-span-2">
                  <span className="font-medium text-slate-600">پکیج انتخاب شده:</span>
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                    <h5 className="font-medium text-blue-800">{selectedPackage.name}</h5>
                    {selectedPackage.description && (
                      <p className="text-sm text-blue-600 mt-1">{selectedPackage.description}</p>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      {selectedPackage.price > selectedPackage.discountPrice && (
                        <span className="text-sm text-gray-500 line-through">
                          {new Intl.NumberFormat('fa-IR').format(selectedPackage.price)} ریال
                        </span>
                      )}
                      <span className="text-sm font-medium text-blue-800">
                        {selectedPackage.discountPrice === 0
                          ? 'رایگان'
                          : `${new Intl.NumberFormat('fa-IR').format(selectedPackage.discountPrice)} ریال`
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {requestDetails.insuranceInfo.type === 'package' && !selectedPackage && (
                <div className="md:col-span-2">
                  <span className="font-medium text-slate-600">پکیج انتخاب شده:</span>
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-600">⚠️</span>
                      <div>
                        <h5 className="font-medium text-yellow-800">شناسه پکیج: {requestDetails.insuranceInfo.selectedPackage}</h5>
                        <p className="text-sm text-yellow-600 mt-1">
                          جزئیات کامل پکیج در دسترس نیست. ممکن است پکیج حذف شده یا تغییر کرده باشد.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {requestDetails.insuranceInfo.description && (
              <div className="mt-4">
                <p className="font-medium text-slate-600 mb-1">توضیحات:</p>
                <p className="bg-slate-50 p-3 rounded-sm border border-slate-200 text-sm">
                  {requestDetails.insuranceInfo.description}
                </p>
              </div>
            )}

            {/* Uploaded Images */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h5 className="font-bold text-slate-700 mb-4">تصاویر آپلود شده</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {renderImagePreview(requestDetails.insuranceInfo.nationalIdImageUrl, 'تصویر کارت ملی')}
                {renderImagePreview(requestDetails.insuranceInfo.testListImageUrl, 'لیست آزمایش‌ها')}
                {renderImagePreview(requestDetails.insuranceInfo.prescriptionImageUrl, 'تصویر نسخه')}
                {renderImagePreview(requestDetails.insuranceInfo.insuranceBookletImageUrl, 'تصویر دفترچه بیمه')}
              </div>
              {!(requestDetails.insuranceInfo.nationalIdImageUrl ||
                requestDetails.insuranceInfo.testListImageUrl ||
                requestDetails.insuranceInfo.prescriptionImageUrl ||
                requestDetails.insuranceInfo.insuranceBookletImageUrl) && (
                  <p className="text-sm text-slate-500">هیچ تصویری برای این درخواست آپلود نشده است.</p>
                )}
            </div>
          </div>

          {/* System Metadata Section */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h5 className="font-bold text-slate-700 mb-4">اطلاعات درخواست</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><span className="font-medium text-slate-600">شناسه درخواست:</span> {requestDetails.displayId || requestDetails._id.slice(-8)}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت ایجاد درخواست:</span> {new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(requestDetails.createdAt))}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت پاسخ آزمایشگاه:</span> {requestDetails.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(requestDetails.labResponseDateTime)) : '-'}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت ویرایش درخواست:</span> {new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(requestDetails.updatedAt))}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت ادامه مراحل درخواست:</span> {requestDetails.continueDateTime ? new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(requestDetails.continueDateTime)) : '-'}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت پرداخت:</span> {requestDetails.paymentDateTime ? new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(requestDetails.paymentDateTime)) : '-'}</div>
              {requestDetails.samplingDate && (
                <div><span className="font-medium text-slate-600">تاریخ نمونه گیری:</span> {formatYmdPersian(requestDetails.samplingDate)}</div>
              )}
              {requestDetails.samplingTime && (
                <div><span className="font-medium text-slate-600">ساعت نمونه گیری:</span> {requestDetails.samplingTime}</div>
              )}
              {requestDetails.amount !== undefined && requestDetails.amount !== null && (
                <div><span className="font-medium text-slate-600">هزینه آزمایش:</span> {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} ریال`}</div>
              )}
              {(requestDetails.status === 'approved' || requestDetails.status === 'paid') && (
                <div><span className="font-medium text-slate-600">هزینه ایاب و ذهاب:</span> {
                  (() => {
                    if (requestDetails.transportationPrice !== undefined && requestDetails.transportationPrice !== null) {
                      return requestDetails.transportationPrice === 0
                        ? 'رایگان'
                        : `${new Intl.NumberFormat('fa-IR').format(requestDetails.transportationPrice)} ریال`;
                    }
                    return requestDetails.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد';
                  })()
                }</div>
              )}
              {(requestDetails.status === 'paid' && requestDetails.amount !== undefined && requestDetails.amount !== null) && (
                <div><span className="font-medium text-slate-600">مجموع پرداختی:</span> {
                  (() => {
                    const amount = requestDetails.amount || 0;
                    const transportation = requestDetails.transportationPrice || 0;
                    const total = amount + transportation;
                    return total === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(total)} ریال`;
                  })()
                }</div>
              )}
            </div>
          </div>

          {/* Transaction History Section */}
          {transactions.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h5 className="font-bold text-slate-700 mb-4">تاریخچه تراکنش‌ها</h5>
              {loadingTransactions ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                  <p className="mt-2 text-sm text-gray-600">در حال بارگیری تراکنش‌ها...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ردیف</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مبلغ</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نوع</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">درگاه</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">کد رهگیری</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((transaction, index) => (
                        <tr key={transaction._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {new Intl.NumberFormat('fa-IR').format(transaction.amount)} ریال
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {transaction.transactionType === 'request' ? 'پرداخت اولیه' : 'پرداخت باقیمانده'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                              }`}>
                              {transaction.status === 'completed' ? 'موفق' :
                                transaction.status === 'failed' ? 'ناموفق' :
                                  'در انتظار'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {transaction.gateway === 'parsian' ? 'پارسیان' : transaction.gateway}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {transaction.refId || '---'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Intl.DateTimeFormat('fa-IR', {
                              year: 'numeric',
                              month: 'numeric',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }).format(new Date(transaction.paymentDate || transaction.createdAt))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      {modalOpen && modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-500"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white p-4 rounded shadow-lg"
            onClick={e => e.stopPropagation()}
          >
            <img src={modalImage} alt="نمایش تصویر" className="max-h-[80vh] max-w-[90vw]" />
            <button
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
              onClick={() => setModalOpen(false)}
            >
              بستن
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ViewRequestStatus;
